// ============================================
// 2026-10-17（标绘层 S2/S3）：图元几何生成（纯函数，可单测）
// 关键点为世界坐标；矩形/圆/椭圆/扇形生成于关键点所在水平面（y 取首点），与「贴地」语义一致。
// S3 新增：椭圆/扇形 + 图元分类（kind 辅助）+ 箭头/曲线/自由线统一 outlineFor。
// ============================================
import { catmullRomCurve } from './curveFit';
import { arrowCurvePoints, arrowFinePoints, arrowStraightPoints, type ArrowStyleOpts } from './arrows';

export type P3 = [number, number, number];

/** 全部标绘图元（S2 基础 + S3 进阶） */
export const ANNOTATION_KINDS = [
  'point', 'text', 'polyline', 'polygon', 'rect', 'circle',
  'ellipse', 'sector', 'freehandLine', 'freehandPolygon', 'curve',
  'arrowStraight', 'arrowFine', 'arrowCurve',
] as const;
export type AnnotationKind = (typeof ANNOTATION_KINDS)[number];

export const KIND_LABELS: Record<string, string> = {
  point: '点位', text: '文字', polyline: '线路', polygon: '区域', rect: '矩形', circle: '圆形',
  ellipse: '椭圆', sector: '扇形', freehandLine: '自由线', freehandPolygon: '自由面', curve: '曲线',
  arrowStraight: '直箭头', arrowFine: '细箭头', arrowCurve: '曲线箭头',
};

export function kindLabel(kind: string): string {
  return KIND_LABELS[kind] ?? kind;
}

/** 箭头族（S3 商用标绘：挂 Pro 的判定依据） */
export function isArrowKind(kind: string): boolean {
  return kind === 'arrowStraight' || kind === 'arrowFine' || kind === 'arrowCurve';
}

/** 面类图元（有填充） */
export function isAreaKind(kind: string): boolean {
  return (
    kind === 'polygon' || kind === 'rect' || kind === 'circle' || kind === 'ellipse' ||
    kind === 'sector' || kind === 'freehandPolygon' || isArrowKind(kind)
  );
}

/** 自由点类图元（关键点即渲染点；支持双击边中点插入 / 右键顶点删除） */
export function canEditVertices(kind: string): boolean {
  return kind === 'polyline' || kind === 'polygon' || kind === 'curve' || kind === 'freehandLine' || kind === 'freehandPolygon';
}

/** 折线渲染点：原样（端点不闭合） */
export function polylinePoints(points: P3[]): P3[] {
  return points.map((p) => [p[0], p[1], p[2]]);
}

/** 多边形/面：闭合（首点追加到末尾，便于描边） */
export function polygonPoints(points: P3[]): P3[] {
  if (points.length < 3) return polylinePoints(points);
  return [...polylinePoints(points), points[0]];
}

/** 矩形四角（对角线两点 a/b；取 a 的 y 保持水平面）：a → (b.x,a.y,a.z) → b → (a.x,a.y,b.z) */
export function rectCorners(a: P3, b: P3): P3[] {
  return [[a[0], a[1], a[2]], [b[0], a[1], a[2]], [b[0], b[1], b[2]], [a[0], a[1], b[2]]];
}

/** 矩形闭合轮廓（四角 + 首角） */
export function rectOutline(a: P3, b: P3): P3[] {
  const c = rectCorners(a, b);
  return [...c, c[0]];
}

/** 圆：中心+边点（半径=水平距离；segments 段），点位于中心的水平面 */
export function circlePoints(center: P3, edge: P3, segments = 64): P3[] {
  const dx = edge[0] - center[0];
  const dz = edge[2] - center[2];
  const r = Math.max(0.05, Math.sqrt(dx * dx + dz * dz));
  const out: P3[] = [];
  for (let i = 0; i <= segments; i++) {
    const t = (i / segments) * Math.PI * 2;
    out.push([center[0] + Math.cos(t) * r, center[1], center[2] + Math.sin(t) * r]);
  }
  return out;
}

/** 椭圆：中心 + 对角点（半轴 = |dx|、|dz|；点位于中心水平面） */
export function ellipsePoints(center: P3, corner: P3, segments = 64): P3[] {
  const rx = Math.max(0.05, Math.abs(corner[0] - center[0]));
  const rz = Math.max(0.05, Math.abs(corner[2] - center[2]));
  const out: P3[] = [];
  for (let i = 0; i <= segments; i++) {
    const t = (i / segments) * Math.PI * 2;
    out.push([center[0] + Math.cos(t) * rx, center[1], center[2] + Math.sin(t) * rz]);
  }
  return out;
}

/** 扇形：圆心 + 起始半径点（定半径/起始角）+ 结束方向点（定结束角）；逆时针扫 ≤ 2π */
export function sectorPoints(center: P3, a: P3, b: P3, segments = 48): P3[] {
  const r = Math.max(0.05, Math.hypot(a[0] - center[0], a[2] - center[2]));
  const a0 = Math.atan2(a[2] - center[2], a[0] - center[0]);
  const a1 = Math.atan2(b[2] - center[2], b[0] - center[0]);
  let sweep = ((a1 - a0) % (Math.PI * 2) + Math.PI * 2) % (Math.PI * 2);
  if (sweep < 1e-3) sweep = Math.PI / 2;
  const out: P3[] = [[center[0], center[1], center[2]]];
  const segs = Math.max(4, Math.floor(segments));
  for (let i = 0; i <= segs; i++) {
    const t = a0 + (sweep * i) / segs;
    out.push([center[0] + Math.cos(t) * r, center[1], center[2] + Math.sin(t) * r]);
  }
  out.push([center[0], center[1], center[2]]);
  return out;
}

/** 图元 → 渲染轮廓点（渲染层/预览/导出统一入口） */
export function outlineFor(kind: string, points: P3[], arrowOpts?: ArrowStyleOpts): P3[] {
  switch (kind) {
    case 'polyline': return polylinePoints(points);
    case 'polygon': return polygonPoints(points);
    case 'rect': return points.length >= 2 ? rectOutline(points[0], points[1]) : polylinePoints(points);
    case 'circle': return points.length >= 2 ? circlePoints(points[0], points[1]) : polylinePoints(points);
    case 'ellipse': return points.length >= 2 ? ellipsePoints(points[0], points[1]) : polylinePoints(points);
    case 'sector': return points.length >= 3 ? sectorPoints(points[0], points[1], points[2]) : polylinePoints(points);
    case 'freehandLine': return polylinePoints(points);
    case 'freehandPolygon': return polygonPoints(points);
    case 'curve': return points.length >= 2 ? catmullRomCurve(points) : polylinePoints(points);
    case 'arrowStraight': return points.length >= 2 ? arrowStraightPoints(points[0], points[1], arrowOpts) : polylinePoints(points);
    case 'arrowFine': return points.length >= 2 ? arrowFinePoints(points[0], points[1], arrowOpts) : polylinePoints(points);
    case 'arrowCurve': return points.length >= 3 ? arrowCurvePoints(points[0], points[1], points[2], arrowOpts) : polylinePoints(points);
    default: return polylinePoints(points);
  }
}

/** 图元绘制所需的点击数（0 = 自由点数，需「完成」按钮/双击结束） */
export function requiredClicks(kind: string): number {
  if (kind === 'point' || kind === 'text') return 1;
  if (kind === 'rect' || kind === 'circle' || kind === 'ellipse' || kind === 'arrowStraight' || kind === 'arrowFine') return 2;
  if (kind === 'sector' || kind === 'arrowCurve') return 3;
  return 0; // polyline / polygon / curve / freehandLine / freehandPolygon
}

/** 自由点数图元绘制中的提示（结束方式） */
export function needsExplicitFinish(kind: string): boolean {
  return kind === 'polyline' || kind === 'polygon' || kind === 'curve';
}
